import React, { useState } from 'react';

import { UploadOutlined } from '@ant-design/icons';
import { Button, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { IUploadFileResponse, useUploadFile } from '../hooks/useUploadFile';
import { useMessage } from '../hooks/useMessage';
import { useDownloadFile } from '../hooks/useDownloadFile';
import { useInteractDownloadFile } from '../hooks/useInteractDownloadFile';
import { IFile } from '../types/file';

export interface IProps {
  label?: string;
  icon?: React.ReactNode | null;
  type?: 'link' | 'primary' | 'text' | 'default' | 'dashed';
  variant?: 'link' | 'text' | 'dashed' | 'outlined' | 'solid' | 'filled';
  size?: 'small' | 'middle' | 'large';
  className?: string;
  style?: React.CSSProperties;
  disabled?: boolean;
  accept?: string;
  multiple?: boolean;
  showUploadList?: boolean;
  files?: IFile[];
  onUploadSuccess?: (file: IUploadFileResponse) => void;
}

export interface IFileUploadFile extends UploadFile {
  path?: string;
  id?: string;
}

const UploadFile: React.FC<IProps> = ({
  label = 'Upload File',
  icon = <UploadOutlined />,
  type = 'primary',
  variant = 'solid',
  size = 'middle',
  className,
  style,
  disabled,
  accept,
  multiple,
  showUploadList,
  files,
  onUploadSuccess,
}) => {
  const message = useMessage();

  const { downloadFile: downloadCoreFile } = useDownloadFile();

  const { downloadFile: downloadInteractFile } = useInteractDownloadFile();

  const [fileList, setFileList] = useState<IFileUploadFile[]>(() => {
    if (files) {
      return files.map(file => ({
        uid: file.id,
        ...file,
        status: 'done',
      }));
    }

    return [];
  });

  const { uploadFileAsync } = useUploadFile();

  const hasUploading = fileList.some(file => file.status === 'uploading');
  const isDisabled = disabled || hasUploading;

  const handleUpload: UploadProps['customRequest'] = async options => {
    const { file, onSuccess, onError } = options;

    try {
      const response = await uploadFileAsync({ file: file as File });

      onSuccess?.(response, file);
      onUploadSuccess?.(response);
    } catch (error) {
      onError?.(error as Error);
    }
  };

  const handleChange: UploadProps['onChange'] = info => {
    if (multiple) {
      setFileList(info.fileList.filter(f => f.status !== 'removed'));
      return;
    }

    if (info.file) {
      if (info.file.status === 'removed') {
        setFileList([]);
        return;
      }
      setFileList([info.file]);
    }
  };

  const handleDownload = (file: IFileUploadFile) => {
    const path = file?.response?.path || file?.path;
    const fileId = file?.response?.id || file?.id;

    if (!path || !fileId) {
      message.error('File not found');
      return;
    }

    if (path.includes('uploads/interact')) {
      downloadInteractFile({ fileId });
      return;
    }

    if (path.includes('model-instances')) {
      const modelInstanceId = path.split('/')[1];
      downloadCoreFile({ fileId, modelInstanceId });
      return;
    }

    message.error('File not found');
  };

  return (
    <Upload
      customRequest={handleUpload}
      accept={accept}
      multiple={multiple}
      showUploadList={
        showUploadList
          ? { showDownloadIcon: true, showRemoveIcon: !disabled }
          : false
      }
      disabled={isDisabled}
      fileList={fileList}
      onChange={handleChange}
      onDownload={handleDownload}
    >
      <Button
        icon={icon}
        type={type}
        variant={variant}
        size={size}
        className={className}
        style={style}
        disabled={isDisabled}
        loading={hasUploading}
      >
        {label}
      </Button>
    </Upload>
  );
};

export default UploadFile;
